/**
 * CR150: Layout Preview Source Hook
 * 
 * Manages the preview source state (Draft | Published Staging | Published Live)
 * with localStorage persistence and auto-switch capabilities.
 */

import { useState, useEffect, useCallback } from "react";

export type PreviewSource = "draft" | "published_staging" | "published_live";

const STORAGE_KEY = "ui.layoutStudio.previewSource";

interface UseLayoutPreviewSourceReturn {
  source: PreviewSource;
  setSource: (source: PreviewSource) => void;
  switchToPublishedStaging: () => void;
  switchToPublishedLive: () => void;
  switchToDraft: () => void;
  isPublishedMode: boolean;
  publishedEnvironment: "staging" | "publish" | null;
}

/**
 * Hook to manage preview source selection with persistence.
 */
export function useLayoutPreviewSource(): UseLayoutPreviewSourceReturn {
  const [source, setSourceState] = useState<PreviewSource>("draft");

  // Load from localStorage on mount (SSR-safe)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "draft" || stored === "published_staging" || stored === "published_live") {
        setSourceState(stored);
      }
    } catch {
      // localStorage not available
    }
  }, []);

  // Persist to localStorage
  const setSource = useCallback((newSource: PreviewSource) => {
    setSourceState(newSource);
    try {
      localStorage.setItem(STORAGE_KEY, newSource);
    } catch {
      // localStorage not available
    }
  }, []);

  const switchToPublishedStaging = useCallback(() => setSource("published_staging"), [setSource]);
  const switchToPublishedLive = useCallback(() => setSource("published_live"), [setSource]);
  const switchToDraft = useCallback(() => setSource("draft"), [setSource]);

  const isPublishedMode = source !== "draft";
  const publishedEnvironment = source === "published_staging" 
    ? "staging" 
    : source === "published_live" 
      ? "publish" 
      : null;

  return {
    source,
    setSource,
    switchToPublishedStaging,
    switchToPublishedLive,
    switchToDraft,
    isPublishedMode,
    publishedEnvironment,
  };
}
